import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Paper } from "@mui/material";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { type MouseEvent as ReactMouseEvent, useEffect, useState } from "react";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { useTableData } from "../../hooks/hooks";
import { useDeleteStrandLossMutation } from "../../hooks/mutations/useStrandLossesManagerMutations";
import {
  STRAND_LOSS_MODAL,
  type StrandLossModal,
  useStrandLossesOptionFunctions,
} from "../../hooks/useStrandLossesOptionFunctions";
import type { StrandAsset } from "../../models/StrandAsset";
import type { StrandLossByShift } from "../../models/StrandLossByShift";
import { strandLossesColors } from "../../theme/colors";
import { UpsertJustifyStrandLoss } from "../modals/UpsertJustifyStrandLoss";

interface StrandLossesManagerTableProps {
  isLoading: boolean;
  strandLosses: StrandLossByShift[];
  parentAssetId: number | null;
  strandAssets: StrandAsset[];
  strandLossId?: number;
}

const CONTEXT_MENU_ID = "strandLossManagerContext";

export const StrandLossesManagerTable = ({
  isLoading: isLoadingGrid,
  strandLosses,
  parentAssetId,
  strandAssets,
  strandLossId,
}: StrandLossesManagerTableProps) => {
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<StrandLossModal>(
    STRAND_LOSS_MODAL.CLOSED,
  );
  const deleteMutation = useDeleteStrandLossMutation();
  const {
    createData,
    justifyData,
    getMenuOptions,
    messageDeleteModal,
    setMessageDeleteModal,
  } = useStrandLossesOptionFunctions({ setSelectedId, setShowModal });
  const { showContextMenu, registerConfig } = useContextMenuMESF();
  const showRowContextMenu = (
    event: ReactMouseEvent<HTMLButtonElement>,
    data: StrandLossByShift,
  ) => showContextMenu(event, data, CONTEXT_MENU_ID);
  const { columnDefs } = useTableData({
    justifyData,
    showContextMenu: showRowContextMenu,
  });

  const handleDeleteStrandLoss = (value?: string) => {
    setShowModal(STRAND_LOSS_MODAL.CLOSED);
    setMessageDeleteModal("");
    if (value !== "OK" || selectedId === null) return;

    deleteMutation.mutate(selectedId, {
      onSuccess: () => {
        setNotificationMessage("Strand Loss deleted successfully");
        setSelectedId(null);
      },
      onError: (mutationError) =>
        setError(
          mutationError instanceof Error
            ? mutationError.message
            : "Failed to delete Strand Loss",
        ),
    });
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams<StrandLossByShift>,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;
    const nativeEvent = (
      params as GetContextMenuItemsParams<StrandLossByShift> & {
        event?: MouseEvent;
      }
    ).event;
    if (nativeEvent && data) {
      showContextMenu(
        nativeEvent as unknown as ReactMouseEvent,
        data,
        CONTEXT_MENU_ID,
      );
    }
    if (data) {
      params.api.deselectAll();
      params.node?.setSelected(true);
    }
    return [];
  };

  useEffect(() => {
    registerConfig({ id: CONTEXT_MENU_ID, getOptions: getMenuOptions });
  }, []);

  const getRowStyle = (params: RowClassParams<StrandLossByShift>) => {
    if (strandLossId && params.data?.StrandLossId === strandLossId) {
      return { background: strandLossesColors.targetedRow };
    }
    if (
      !params.data?.StrandLossCodeName ||
      !params.data.StrandLossCategoryName
    ) {
      return { background: strandLossesColors.unclassifiedRow };
    }
    return undefined;
  };

  const getRowClass = (params: RowClassParams<StrandLossByShift>) => {
    if (strandLossId && params.data?.StrandLossId === strandLossId) return "";
    return !params.data?.StrandLossCodeName ||
      !params.data.StrandLossCategoryName
      ? "unclassified-row"
      : "";
  };

  return (
    <>
      <div onContextMenu={(event) => event.preventDefault()}>
        <Grid container>
          <Grid
            component={Paper}
            elevation={3}
            item
            md={12}
            xs={12}
            sx={{
              p: 1.25,
              height: "calc(100vh - 300px)",
              minHeight: "calc(100vh - 300px)",
            }}
          >
            <AgGridReact<StrandLossByShift>
              rowData={strandLosses}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getContextMenuItems={getContextMenuItems}
              onCellDoubleClicked={(event) =>
                event.data && justifyData(event.data)
              }
              getRowId={(params) => String(params.data.StrandLossId)}
              loading={isLoadingGrid}
              getRowStyle={getRowStyle}
              getRowClass={getRowClass}
              {...defaultGridOptions}
              suppressContextMenu={false}
            />
            <Grid container justifyContent="flex-end">
              <Grid
                item
                md={2}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3 }}
                  onClick={createData}
                >
                  NEW STRAND LOSS
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <ConfirmationDialogRaw
        title="Confirmation"
        message={messageDeleteModal}
        open={showModal === STRAND_LOSS_MODAL.DELETE}
        onClose={handleDeleteStrandLoss}
      />
      <UpsertJustifyStrandLoss
        showModal={showModal}
        onHide={() => {
          setShowModal(STRAND_LOSS_MODAL.CLOSED);
          setSelectedId(null);
        }}
        strandLossId={selectedId}
        parentAssetId={parentAssetId}
        strandAssets={strandAssets}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
